import type { Achievement } from "@/types/api";
import { useLang } from "@/i18n/LangContext";


interface AchievementsSectionProps {
  appid: string;
  achievements: Achievement[];
  totalAchievements: number;
}

export default function AchievementsSection({ achievements, totalAchievements }: AchievementsSectionProps) {
  const { t } = useLang();

  if (achievements.length === 0) return null;

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.achievements}</h2>
        <span className="text-xs text-gray-400 dark:text-muted ml-auto">
          {totalAchievements > 0 ? t.gameDetail.showingOf.replace("{total}", String(totalAchievements)).replace("{count}", String(achievements.length)) : t.gameDetail.showingCount.replace("{count}", String(achievements.length))}
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-muted mb-4 leading-relaxed">
        {t.gameDetail.achievementsDesc}
      </p>
      <div className="flex flex-col gap-2">
        {achievements.map((ach, idx) => {
          const pct = Number(ach.percent) || 0;
          const rarityLabel = pct >= 50 ? t.gameDetail.common : pct >= 20 ? t.gameDetail.uncommon : pct >= 5 ? t.gameDetail.rare : t.gameDetail.veryRare;
          const rarityColor = pct >= 50 ? "text-gray-400" : pct >= 20 ? "text-blue-400" : pct >= 5 ? "text-amber-500" : "text-violet-500";
          return (
            <div key={`${ach.name}-${idx}`} className="flex items-center gap-3 bg-inner-bg dark:bg-inner-bg rounded-xl px-4 py-3 border border-inner-border dark:border-card-border">
              {ach.icon && (
                <img
                  src={ach.icon}
                  alt={ach.name}
                  width={32}
                  height={32}
                  className="rounded shrink-0"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ach.name}</p>
                {ach.description ? (
                  <p className="text-xs text-gray-500 dark:text-muted truncate">{ach.description}</p>
                ) : ach.hidden ? (
                  <p className="text-xs text-gray-400 dark:text-muted italic">{t.gameDetail.hiddenAchievement}</p>
                ) : null}
                <p className={`text-[10px] font-bold ${rarityColor}`}>{rarityLabel}</p>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                <div className="w-24 h-2 bg-gray-200 dark:bg-inner-bg rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      pct >= 50 ? "bg-emerald-500" : pct >= 20 ? "bg-blue-500" : pct >= 5 ? "bg-amber-500" : "bg-violet-500"
                    }`}
                    style={{ width: `${Math.max(pct, 1)}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-gray-600 dark:text-muted tabular-nums w-12 text-right">
                  {pct.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
